import { useEffect } from 'react'
import clsx from 'clsx'

/**
 * 弹窗外壳。
 *
 * 建这个组件是因为全站 16 处弹窗各写一遍 `fixed inset-0 flex items-center`，
 * 其中 15 处既没限高也没内部滚动：内容比屏幕高时会从上下两端同时溢出，
 * 顶部那截连滚都滚不回来（items-center 居中比 items-start 更糟）。
 * 移动端弹窗一有表单或长文就会踩到。
 *
 * 这里统一保证三件事：限高、内部可滚、打开时锁背景滚动。
 *
 * size: sm(24rem) / md(28rem，默认) / lg(36rem) / xl(48rem)
 */
const SIZES = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
}

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closable = true,
  className,
}) {
  // 打开时锁背景滚动，否则手机上滑弹窗会带着后面的页面一起动
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  useEffect(() => {
    if (!open || !closable) return
    const onKey = (e) => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, closable, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overscroll-contain bg-black/50 p-4"
      onClick={closable ? onClose : undefined}
    >
      {/* my-auto + max-h 一起用：内容矮时居中，内容高时贴上下留白并在内部滚动 */}
      <div
        className={clsx(
          'my-auto w-full max-h-[calc(100dvh-2rem)] flex flex-col bg-white rounded-xl shadow-2xl',
          SIZES[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between gap-3 px-4 py-4 sm:px-6 border-b shrink-0">
            <h4 className="font-semibold">{title}</h4>
            {closable && (
              <button
                type="button"
                onClick={onClose}
                aria-label="关闭"
                className="flex items-center justify-center w-9 h-9 -mr-1 shrink-0 text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            )}
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-6 sm:p-6">{children}</div>

        {footer && (
          <div className="px-4 py-4 sm:px-6 border-t shrink-0">{footer}</div>
        )}
      </div>
    </div>
  )
}
